// src/components/TeamItemsPage/CalendarView.jsx

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styles from './CalendarView.module.css';
import { FaChevronLeft, FaChevronRight, FaEye, FaEdit } from 'react-icons/fa';
import { 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addDays, 
  isSameMonth, 
  isSameDay, 
  format, 
  addMonths, 
  subMonths 
} from 'date-fns';

const CalendarView = ({ items, onEdit, onQuickView, canManage }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Group items by date
  const itemsByDate = useMemo(() => {
    const map = new Map();
    items.forEach(item => {
      if (item.dueDate) {
        const dateKey = format(new Date(item.dueDate), 'yyyy-MM-dd');
        if (!map.has(dateKey)) {
          map.set(dateKey, []);
        }
        map.get(dateKey).push(item);
      }
    });
    return map;
  }, [items]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  }, [currentMonth]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return '#dc3545';
      case 'Medium': return '#ffc107';
      case 'Low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const renderDay = (day) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    const dayItems = itemsByDate.get(dateKey) || [];
    const isCurrentMonth = isSameMonth(day, currentMonth);
    const isToday = isSameDay(day, new Date());

    return (
      <div
        key={day.toString()}
        className={`${styles.calendarDay} ${!isCurrentMonth ? styles.otherMonth : ''} ${isToday ? styles.today : ''}`}
      >
        <div className={styles.dayHeader}>
          <span className={styles.dayNumber}>{format(day, 'd')}</span>
        </div>
        <div className={styles.dayContent}>
          {dayItems.map(item => (
            <div
              key={item._id}
              className={styles.calendarItem}
              style={{ borderLeftColor: getPriorityColor(item.priority) }}
              onClick={() => onQuickView(item)}
            >
              <div className={styles.itemTitle}>{item.title}</div>
              {item.assignedTo && (
                <div className={styles.itemAssignee}>
                  {item.assignedTo.firstName}
                </div>
              )}
              <div className={styles.itemActions}>
                <button
                  className={styles.itemActionBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuickView(item);
                  }}
                  title="View"
                >
                  <FaEye />
                </button>
                {canManage && (
                  <button
                    className={styles.itemActionBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(item);
                    }}
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.calendarContainer}>
      {/* Calendar Header */}
      <div className={styles.calendarHeader}>
        <button
          className={styles.navBtn}
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
        >
          <FaChevronLeft />
        </button>
        <h2 className={styles.monthTitle}>
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <button
          className={styles.navBtn}
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Day Labels */}
      <div className={styles.weekDays}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className={styles.weekDay}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className={styles.calendarGrid}>
        {calendarDays.map(day => renderDay(day))}
      </div>

      {/* Items without due dates */}
      {items.filter(item => !item.dueDate).length > 0 && (
        <div className={styles.unscheduledSection}>
          <h3 className={styles.unscheduledTitle}>No Due Date</h3>
          <div className={styles.unscheduledItems}>
            {items.filter(item => !item.dueDate).map(item => (
              <div
                key={item._id}
                className={styles.unscheduledItem}
                style={{ borderLeftColor: getPriorityColor(item.priority) }}
              >
                <div className={styles.itemTitle}>{item.title}</div>
                <div className={styles.itemMeta}>
                  <span className={styles.priorityBadge}>
                    {item.priority}
                  </span>
                  {item.assignedTo && (
                    <span className={styles.assigneeName}>
                      {item.assignedTo.firstName} {item.assignedTo.lastName}
                    </span>
                  )}
                </div>
                <div className={styles.itemActions}>
                  <button
                    className={styles.itemActionBtn}
                    onClick={() => onQuickView(item)}
                  >
                    <FaEye /> View
                  </button>
                  {canManage && (
                    <button
                      className={styles.itemActionBtn}
                      onClick={() => onEdit(item)}
                    >
                      <FaEdit /> Edit
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

CalendarView.propTypes = {
  items: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onQuickView: PropTypes.func.isRequired,
  canManage: PropTypes.bool
};

export default CalendarView;

